import { useEffect, useState } from "react";
import { api, formatApiError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";

const EMPTY = { nom: "", prenom: "", titre: "", service: "", naissance: "", date_formation: "", fin_validite: "", proposition_mac: "", valide_mac: "" };

export default function EmployeeDialog({ open, onOpenChange, employee, services, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(employee ? {
        nom: employee.nom, prenom: employee.prenom, titre: employee.titre || "", service: employee.service,
        naissance: employee.naissance || "", date_formation: employee.date_formation || "",
        fin_validite: employee.fin_validite || "", proposition_mac: employee.proposition_mac || "",
        valide_mac: employee.valide_mac || "",
      } : EMPTY);
    }
  }, [open, employee]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async () => {
    if (!form.nom.trim() || !form.prenom.trim() || !form.service.trim()) {
      toast.error("Nom, prénom et service sont obligatoires");
      return;
    }
    setSaving(true);
    const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    payload.titre = form.titre || "";
    payload.service = form.service;
    try {
      if (employee) {
        await api.put(`/employees/${employee.id}`, payload);
        toast.success("Salarié mis à jour");
      } else {
        await api.post("/employees", payload);
        toast.success("Salarié ajouté");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{employee ? "Modifier le salarié" : "Ajouter un salarié"}</DialogTitle>
          <DialogDescription>Les champs marqués d'un astérisque sont obligatoires. La fin de validité est calculée automatiquement (formation + 24 mois) si elle n'est pas renseignée.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label>Nom *</Label>
            <Input value={form.nom} onChange={set("nom")} data-testid="employee-form-nom" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Prénom *</Label>
            <Input value={form.prenom} onChange={set("prenom")} data-testid="employee-form-prenom" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Civilité</Label>
            <Select value={form.titre || "none"} onValueChange={(v) => setForm({ ...form, titre: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="employee-form-titre" className="bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="M">M</SelectItem>
                <SelectItem value="MME">MME</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Service *</Label>
            <Input value={form.service} onChange={set("service")} list="services-list" placeholder="ex : 92 SRV"
              data-testid="employee-form-service" className="bg-background" />
            <datalist id="services-list">
              {services.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label>Date de naissance</Label>
            <Input type="date" value={form.naissance} onChange={set("naissance")} data-testid="employee-form-naissance" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Date formation SST</Label>
            <Input type="date" value={form.date_formation} onChange={set("date_formation")} data-testid="employee-form-formation" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Fin de validité <span className="text-muted-foreground text-xs">(auto : formation + 24 mois)</span></Label>
            <Input type="date" value={form.fin_validite} onChange={set("fin_validite")} data-testid="employee-form-validite" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Date MAC proposée</Label>
            <Input type="date" value={form.proposition_mac} onChange={set("proposition_mac")} data-testid="employee-form-mac" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>MAC validée ?</Label>
            <Select value={form.valide_mac || "none"} onValueChange={(v) => setForm({ ...form, valide_mac: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="employee-form-valide" className="bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="Oui">Oui</SelectItem>
                <SelectItem value="Non">Non</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="employee-form-submit">
            {saving ? "Enregistrement..." : employee ? "Mettre à jour" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
